import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "design_system_radar/button";
import { Card } from "design_system_radar/card";
import { RadarLogo } from "design_system_radar/icons/RadarLogo";
import { Input } from "design_system_radar/input";
import { Label } from "design_system_radar/label";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useNavigate } from "react-router";

import { ENV } from "@/config/environment";
import { preloadRouteModules } from "@/config/routing/browserRoutes";
import { routes } from "@/config/routing/routes";
import useQuery from "@/hooks/useQuery";
import useAuth from "@/modules/auth/useAuth";
import type { NewPasswordChallenge } from "@/services/auth";
import { getMissingCognitoConfig } from "@/services/auth";

type SignInStep = "email" | "password";

const invalidCredentialsMessages = new Set([
  "Credenciales inválidas. Revisa tu correo y contraseña.",
  "No existe una cuenta con ese correo.",
]);

const environmentBadgeLabel = ENV?.trim().toUpperCase();
const environmentBadgeEnv = ENV?.trim().toLowerCase();
const shouldShowEnvironmentBadge =
  environmentBadgeEnv === "dev" || environmentBadgeEnv === "test";

const SignIn = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { login } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const hasPreloadedRouteRef = useRef(false);
  const [step, setStep] = useState<SignInStep>("email");
  const usernameFromQuery = query.get("username")?.trim() || "";
  const [username, setUsername] = useState(usernameFromQuery);
  const [password, setPassword] = useState("");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const returnTo = query.get("returnTo") || routes.admin.absolute;
  const missingConfig = getMissingCognitoConfig();

  const hasInlinePasswordError = useMemo(() => {
    return runtimeError ? invalidCredentialsMessages.has(runtimeError) : false;
  }, [runtimeError]);

  const hasInlineEmailError = step === "email" && runtimeError !== null;
  const shouldRevealPassword = step === "password" || Boolean(password);

  const getInputValue = useCallback((fieldName: "username" | "password") => {
    const input = formRef.current?.elements.namedItem(fieldName);

    return input instanceof HTMLInputElement ? input.value : "";
  }, []);

  const getCredentials = () => {
    return {
      password: getInputValue("password") || password,
      username: getInputValue("username").trim() || username.trim(),
    };
  };

  useEffect(() => {
    const syncAutofilledValues = () => {
      const nextUsername = getInputValue("username");
      const nextPassword = getInputValue("password");

      if (nextUsername !== username) {
        setUsername(nextUsername);
      }

      if (nextPassword !== password) {
        setPassword(nextPassword);
      }

      if (
        runtimeError &&
        (nextUsername !== username || nextPassword !== password)
      ) {
        setRuntimeError(null);
      }

      if (nextPassword && step === "email") {
        setStep("password");
      }
    };

    syncAutofilledValues();

    const form = formRef.current;

    if (!form) {
      return;
    }

    const inputs = ["username", "password"]
      .map((fieldName) => form.elements.namedItem(fieldName))
      .filter(
        (input): input is HTMLInputElement => input instanceof HTMLInputElement,
      );

    for (const input of inputs) {
      input.addEventListener("input", syncAutofilledValues);
      input.addEventListener("change", syncAutofilledValues);
    }

    const intervalId = window.setInterval(syncAutofilledValues, 250);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 3000);

    return () => {
      for (const input of inputs) {
        input.removeEventListener("input", syncAutofilledValues);
        input.removeEventListener("change", syncAutofilledValues);
      }

      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [getInputValue, password, runtimeError, step, username]);

  useEffect(() => {
    if (usernameFromQuery) {
      setUsername(usernameFromQuery);
      setStep("password");
    }
  }, [usernameFromQuery]);

  useEffect(() => {
    if (step !== "password") {
      return;
    }

    usernameInputRef.current?.blur();
    passwordInputRef.current?.focus();

    const passwordLength = passwordInputRef.current?.value.length ?? 0;
    passwordInputRef.current?.setSelectionRange(passwordLength, passwordLength);
  }, [step]);

  const resetStateForEmailStep = () => {
    setUsername("");
    setPassword("");
    setRuntimeError(null);
    setShowPassword(false);
    setStep("email");
    if (passwordInputRef.current) {
      passwordInputRef.current.value = "";
    }
  };

  const preloadDefaultRoute = useCallback(() => {
    if (hasPreloadedRouteRef.current) {
      return;
    }

    hasPreloadedRouteRef.current = true;
    void preloadRouteModules(returnTo);
  }, [returnTo]);

  const getUsernameError = (usernameValue: string) => {
    if (!usernameValue) {
      return "Ingresa tu correo corporativo para continuar.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(usernameValue)) {
      return "Ingresa un correo electrónico válido.";
    }

    return null;
  };

  const handleContinue = (usernameValue: string) => {
    const usernameError = getUsernameError(usernameValue);

    if (usernameError) {
      setRuntimeError(usernameError);
      return;
    }

    setRuntimeError(null);
    setStep("password");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const credentials = getCredentials();

    if (credentials.username !== username) {
      setUsername(credentials.username);
    }

    if (credentials.password !== password) {
      setPassword(credentials.password);
    }

    const usernameError = getUsernameError(credentials.username);

    if (usernameError) {
      setRuntimeError(usernameError);
      return;
    }

    if (step === "email") {
      handleContinue(credentials.username);
      return;
    }

    if (!credentials.password) {
      setStep("password");
      setRuntimeError("Ingresa tu contraseña para continuar.");
      return;
    }

    setRuntimeError(null);
    setIsSubmitting(true);

    try {
      const result = await login?.({
        password: credentials.password,
        username: credentials.username,
      });

      const challenge = result as NewPasswordChallenge | undefined;
      if (challenge?.type === "NEW_PASSWORD_REQUIRED") {
        navigate(
          `${routes.resetPassword.absolute}?session=${encodeURIComponent(challenge.session)}&username=${encodeURIComponent(credentials.username)}`,
        );
        return;
      }

      await preloadRouteModules(returnTo);
      navigate(returnTo, { replace: true });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No fue posible iniciar sesión con Cognito.";
      setRuntimeError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      className="flex min-h-screen flex-col justify-center bg-[#171821] px-5 py-7 text-white sm:px-8 sm:py-10 lg:px-14 lg:py-12"
      data-radar-environment={environmentBadgeEnv}
      role="main"
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          {/* Form */}
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-90">
              {shouldShowEnvironmentBadge ? (
                <div className="mb-8">
                  <span className="auth-env-badge">
                    {environmentBadgeLabel}
                  </span>
                </div>
              ) : null}

              <h2 className="text-[28px] font-semibold tracking-[-0.03em] text-white">
                Inicia sesión
              </h2>

              {step === "email" ? (
                <p className="mt-4 text-[14px] leading-6 text-white/72">
                  Ingresa los datos indicados a continuación.
                </p>
              ) : (
                <div className="mt-4 flex items-center gap-2 text-[12px] text-white/78">
                  <span className="max-w-55 truncate">{username.trim()}</span>
                  <button
                    className="font-semibold text-white transition hover:text-[#a9dfd8]"
                    type="button"
                    onClick={resetStateForEmailStep}
                  >
                    Cambiar
                  </button>
                </div>
              )}

              {missingConfig.length > 0 && (
                <div
                  className="mt-7 rounded-md border border-amber-300/20 bg-amber-500/10 px-4 py-3 text-[12px] leading-5 text-amber-100"
                  role="alert"
                >
                  Faltan variables de entorno para Cognito:{" "}
                  {missingConfig.join(", ")}.
                </div>
              )}

              {!hasInlinePasswordError &&
                !hasInlineEmailError &&
                runtimeError && (
                  <div
                    className="mt-7 rounded-md border border-red-400/20 bg-red-500/10 px-4 py-3 text-[12px] leading-5 text-red-100"
                    role="alert"
                  >
                    {runtimeError}
                  </div>
                )}

              <form
                data-auth
                className="mt-16 space-y-14"
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {/* Email field — stays mounted for password managers, but collapses on the password step */}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ${
                    step === "email"
                      ? "grid-rows-[1fr] opacity-100"
                      : "pointer-events-none grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="space-y-3 overflow-hidden">
                    <Label
                      className="text-[11px] font-medium tracking-[0.04em] text-white/80"
                      htmlFor="username"
                    >
                      Correo electrónico
                    </Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70" />
                      <Input
                        autoComplete="username"
                        className={`h-10 rounded-none border-0 border-b bg-transparent pl-7 pr-0 text-[14px] text-white shadow-none ring-0 placeholder:text-white/50 focus-visible:ring-0 ${
                          hasInlineEmailError
                            ? "border-[#ff2b5f] text-[#ff3d6d]"
                            : "border-white"
                        }`}
                        id="username"
                        name="username"
                        placeholder="usuario@mail.com"
                        readOnly={step !== "email"}
                        ref={usernameInputRef}
                        type="email"
                        value={username}
                        onFocus={preloadDefaultRoute}
                        onChange={(event) => {
                          setUsername(event.target.value);
                          if (runtimeError) {
                            setRuntimeError(null);
                          }
                        }}
                      />
                    </div>
                    <div className="min-h-5 text-[11px] leading-5">
                      {hasInlineEmailError ? (
                        <span className="text-[#ff2b5f]">{runtimeError}</span>
                      ) : null}
                    </div>
                    <div className="flex justify-end">
                      <button
                        className="text-[11px] font-semibold text-white transition hover:text-[#a9dfd8]"
                        type="button"
                        onClick={() =>
                          navigate(
                            username.trim()
                              ? `${routes.sendResetPassword.absolute}?username=${encodeURIComponent(
                                  username.trim(),
                                )}`
                              : routes.sendResetPassword.absolute,
                          )
                        }
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                  </div>
                </div>

                {/* Password field — stays mounted for password managers, but remains visually collapsed until needed */}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ${
                    shouldRevealPassword
                      ? "grid-rows-[1fr] opacity-100"
                      : "pointer-events-none grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="space-y-3 overflow-hidden">
                    <Label
                      className="text-[11px] font-medium tracking-[0.04em] text-white/80"
                      htmlFor="password"
                    >
                      Contraseña
                    </Label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70" />
                      <Input
                        autoComplete="current-password"
                        className={`h-10 rounded-none border-0 border-b bg-transparent pl-7 pr-8 text-[14px] text-white shadow-none ring-0 placeholder:text-white/50 focus-visible:ring-0 ${
                          hasInlinePasswordError
                            ? "border-[#ff2b5f] text-[#ff3d6d]"
                            : "border-white"
                        }`}
                        id="password"
                        name="password"
                        placeholder="Ingresa tu contraseña"
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          if (runtimeError) {
                            setRuntimeError(null);
                          }
                        }}
                      />
                      <button
                        aria-label={
                          showPassword
                            ? "Ocultar contraseña"
                            : "Mostrar contraseña"
                        }
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-white/65 transition hover:text-white"
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <div className="min-h-5 text-[11px] leading-5">
                      {hasInlinePasswordError ? (
                        <span className="text-[#ff2b5f]">
                          Contraseña incorrecta
                        </span>
                      ) : null}
                    </div>
                    <div className="flex justify-end">
                      <button
                        className="text-[11px] font-semibold text-white transition hover:text-[#a9dfd8]"
                        type="button"
                        onClick={() =>
                          navigate(
                            username.trim()
                              ? `${routes.sendResetPassword.absolute}?username=${encodeURIComponent(
                                  username.trim(),
                                )}`
                              : routes.sendResetPassword.absolute,
                          )
                        }
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-12">
                  <Button
                    className={`h-10.5 w-full rounded-lg text-[14px] font-semibold shadow-none ${
                      isSubmitting || missingConfig.length > 0
                        ? "bg-[#7f7f84] text-[#2f3d5c] hover:bg-[#7f7f84]"
                        : "bg-[#a9dfd8] text-[#24314f] hover:bg-[#9dd6d0]"
                    }`}
                    disabled={
                      isSubmitting ||
                      missingConfig.length > 0 ||
                      (step === "email" ? !username.trim() : !password)
                    }
                    size="lg"
                    type="submit"
                    variant="primary"
                  >
                    {isSubmitting ? "Validando..." : "Continuar"}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Image card */}
          <div className="flex items-center justify-center">
            <Card className="relative h-200 w-full max-w-184 overflow-hidden rounded-[12px] border-0 bg-white">
              <picture>
                <source srcSet="/radar-bg.avif" type="image/avif" />
                <source srcSet="/radar-bg.webp" type="image/webp" />
                <img
                  src="/radar-bg.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover"
                  fetchPriority="high"
                  height={1632}
                  loading="eager"
                  width={2912}
                />
              </picture>
              <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center px-8">
                <RadarLogo
                  size={400}
                  fill="#fff"
                  stroke="rgba(0, 0, 0, 0.85)"
                  strokeWidth={2}
                  className="h-auto w-full max-w-100"
                  aria-label="Radar"
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignIn;
